import React, { useEffect, useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';

import { FilesFilter } from 'components/organisms/Files';
import { UploadResourcesModal } from 'components/organisms/Task';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useTask } from 'contexts/task';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import { useWebsocket } from 'contexts/websocket';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'pages/Files/mobile';
import Tablet from 'pages/Files/tablet';
import Desktop from 'pages/Files/desktop';
import groupService from 'services/group';
import { AlertDialog, Drawer, IconButton, Stack } from 'shell/components';
import { TYPES } from 'utils/constants';
import { getAbsoluteURL, getResourceName, getS3FileURL } from 'utils/helper';

const FilesPage = () => {
    const { auth } = useAuth();
    const { message } = useSnackbar();
    const { getTaskById } = useTask();
    const { isLoading, course, currentGroup, accountUsers } = useUser2();
    const { device, width } = useViewport();
    const { sendOn } = useWebsocket();

    const [resources, setResources] = useState(null);
    const [checked, setChecked] = useState([]);
    const [upload, setUpload] = useState({ open: false, isSubmitting: false });
    const [filters, setFilters] = useState({
        open: false,
        sort: 'latest',
        types: [],
        owners: [],
        searchInput: '',
    });
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        onAction: () => {},
    });

    let numFilters = 0;
    if (filters.sort !== 'latest') numFilters++;
    if (filters.types.length) numFilters++;
    if (filters.owners.length) numFilters++;

    const getSortedFilteredResources = (resources) => {
        return (resources || [])
            .filter((resource) => {
                if (!filters.types.length) return true;
                const resourceType = resource.data ? 'file' : 'link';
                return filters.types.includes(resourceType);
            })
            .filter((resource) => {
                if (!filters.owners.length) return true;
                return filters.owners.includes(resource.owner_account_id);
            })
            .filter((resource) => {
                const name = getResourceName(resource);
                return name.toLowerCase().includes(filters.searchInput.toLowerCase());
            })
            .sort((a, b) => {
                const nameA = getResourceName(a).toLowerCase();
                const nameB = getResourceName(b).toLowerCase();
                if (filters.sort === 'latest') {
                    return b.created_on - a.created_on;
                }
                if (filters.sort === 'earliest') {
                    return a.created_on - b.created_on;
                }
                if (filters.sort === 'namea-z') {
                    return nameA > nameB ? 1 : -1;
                }
                if (filters.sort === 'namez-a') {
                    return nameA < nameB ? 1 : -1;
                }
            });
    };

    const handleUpload = async (files, link) => {
        setUpload((current) => ({ ...current, isSubmitting: true }));
        if (files.length) {
            const response = await groupService.createGroupFile(currentGroup.id, {
                data: files[0],
                active: true,
                created_on: new Date().getTime(),
                type: TYPES.GROUP,
                public: true,
                course_id: course.id,
                group_id: currentGroup.id,
            });
            setResources((current) => [...current, response]);
        }
        if (link.name && link.url) {
            const response = await groupService.createGroupSharedUrl(currentGroup.id, {
                active: true,
                created_on: new Date().getTime(),
                url: link.url.trim(),
                name: link.name.trim(),
                type: TYPES.GROUP,
                public: true,
                course_id: course.id,
                group_id: currentGroup.id,
            });
            setResources((current) => [...current, response]);
        }
        setUpload((current) => ({ ...current, open: false }));
    };

    const handleWSUpdates = (files) => {
        files.forEach((file) => {
            const taskId = file.task_id || file.grand_parent_id;
            const task = getTaskById(taskId);
            if (task) {
                sendOn('default', {
                    payload_type: 'update_task',
                    payload: {
                        task_id: taskId,
                        group_id: currentGroup.id,
                        key: 'file_num',
                        old_value: getTaskById(taskId).file_num,
                        new_value: getTaskById(taskId).file_num - 1,
                    },
                });
            }
        });
    };

    const deleteResources = async (resourceIds) => {
        const toDelete = resourceIds.map((id) => resources.find((resource) => resource.id === id));
        const filesToDelete = toDelete.filter(({ data }) => data);
        const sharedUrlsToDelete = toDelete.filter(({ data }) => !data);

        if (filesToDelete.length) {
            await groupService.deleteGroupFiles(currentGroup.id, {
                files: filesToDelete.map(({ id, type }) => ({ id, type })),
            });
            handleWSUpdates(filesToDelete.filter(({ type }) => type === TYPES.FEEDBACK));
        }
        if (sharedUrlsToDelete.length) {
            await groupService.deleteGroupSharedUrls(currentGroup.id, {
                shared_urls: sharedUrlsToDelete.map(({ id, type }) => ({ id, type })),
            });
        }

        setResources((current) => current.filter(({ id }) => !resourceIds.includes(id)));
        setChecked([]);
        message.success(`${resourceIds.length} file(s)/website link(s) successfully deleted.`);
    };

    const onDelete = (resource) => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Permanently Delete ${resource.data ? 'File' : 'Website Link'}?`,
            description: `Are you sure you want to delete this ${resource.data ? 'file' : 'website link'}?`,
            onAction: () => {
                deleteResources([resource.id]);
                setDialog((current) => ({
                    ...current,
                    open: false,
                    onAction: () => {},
                }));
            },
        }));
    };

    const onDeleteMany = () => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Permanently Delete ${checked.length} Files/Website Links?`,
            description: `Are you sure you want to delete these files/website links?`,
            onAction: () => {
                deleteResources(checked);
                setDialog((current) => ({
                    ...current,
                    open: false,
                    onAction: () => {},
                }));
            },
        }));
    };

    const getResourceURL = (resource) => {
        if (resource.url) {
            return getAbsoluteURL(resource.url);
        } else {
            return getS3FileURL(resource.data);
        }
    };

    const renderResourceActions = (resource, onClickDownload, onClickDelete) => {
        return (
            <Stack direction={'row'}>
                <IconButton
                    kind={resource.url ? 'link' : 'download'}
                    size={'medium'}
                    tooltip={{ component: resource.url ? 'Open link' : 'Download file' }}
                    variant={'square'}
                    onClick={onClickDownload}
                />
                <IconButton
                    kind={'delete'}
                    size={'medium'}
                    tooltip={{ component: 'Delete' }}
                    variant={'square'}
                    onClick={onClickDelete}
                />
            </Stack>
        );
    };

    useEffect(() => {
        if (currentGroup?.id) {
            (async () => {
                const files = await groupService.listGroupFiles(currentGroup.id);
                const sharedUrls = await groupService.listGroupSharedUrls(currentGroup.id);
                setResources([...files, ...sharedUrls]);
            })();
        }
    }, [auth.course_id, currentGroup?.id]);

    useEffect(() => {
        if (!upload.open) {
            setTimeout(() => {
                setUpload((current) => ({ ...current, isSubmitting: false }));
            }, 150);
        }
    }, [upload.open]);

    const props = {
        isLoading: !Boolean(resources),
        resources: getSortedFilteredResources(resources),
        accountUsers,
        checked,
        setChecked,
        setUpload,
        filters,
        setFilters,
        numFilters,
        onDelete,
        onDeleteMany,
        getResourceURL,
        renderResourceActions,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const filterDrawerProps = {
        open: filters.open,
        onClose: () => setFilters((current) => ({ ...current, open: false })),
    };

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                <FilesFilter filters={filters} setFilters={setFilters} />
            </Drawer>
        ),
        tablet: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                <FilesFilter filters={filters} setFilters={setFilters} />
            </Drawer>
        ),
        desktop: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                <FilesFilter filters={filters} setFilters={setFilters} />
            </Drawer>
        ),
    };

    return isLoading ? (
        <LogoLoading />
    ) : (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveFilterDrawers)}
            <UploadResourcesModal
                device={device}
                isOpen={upload.open}
                isSubmitting={upload.isSubmitting}
                submitResources={handleUpload}
                onClose={() => setUpload((current) => ({ ...current, open: false }))}
            />
            <AlertDialog
                open={dialog.open}
                message={dialog.message}
                description={dialog.description}
                variant={'destructive'}
                actionButtonText={'Delete'}
                onAction={dialog.onAction}
                onCancel={() => setDialog((current) => ({ ...current, open: false }))}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
            />
        </>
    );
};

export default FilesPage;
